import { arrayMove } from '@dnd-kit/sortable'
import { type Chapter } from '@shared/chapterSectionsOrder'
import { useCallback, useMemo, useRef, useState } from 'react'

const UNATTACHED_ID = '__unattached__'

interface DraftState {
  chapters: Chapter[]
  unattached: string[]
}

export function useOrganizeChaptersState(
  initialChapters: Chapter[],
  initialUnattached: string[],
  initialSelectedKey?: string | null,
) {
  const [draft, setDraft] = useState<DraftState>(() => ({
    chapters: deepClone(initialChapters),
    unattached: [...initialUnattached],
  }))
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() =>
    initialSelectedKey ? new Set([initialSelectedKey]) : new Set(),
  )
  const lastClickedKeyRef = useRef<string | null>(null)
  const undoStackRef = useRef<DraftState[]>([])
  const originalRef = useRef<DraftState>({ chapters: initialChapters, unattached: initialUnattached })

  const dirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(originalRef.current)
  }, [draft])

  const pushUndo = useCallback(() => {
    undoStackRef.current.push(deepClone(draft))
  }, [draft])

  // --- Selection ---

  /** Get all section keys in order (all chapters then unattached) */
  const allKeysInOrder = useMemo(() => {
    const keys: string[] = []
    for (const ch of draft.chapters) {
      keys.push(...ch.chapterSections)
    }
    keys.push(...draft.unattached)
    return keys
  }, [draft])

  const toggleSelection = useCallback(
    (key: string, shiftKey: boolean, metaKey: boolean) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev)

        if (shiftKey && lastClickedKeyRef.current) {
          // Range select
          const allKeys = allKeysInOrder
          const fromIdx = allKeys.indexOf(lastClickedKeyRef.current)
          const toIdx = allKeys.indexOf(key)
          if (fromIdx !== -1 && toIdx !== -1) {
            const start = Math.min(fromIdx, toIdx)
            const end = Math.max(fromIdx, toIdx)
            for (let i = start; i <= end; i++) {
              next.add(allKeys[i])
            }
          }
        } else if (metaKey) {
          // Toggle individual
          if (next.has(key)) {
            next.delete(key)
          } else {
            next.add(key)
          }
        } else {
          // Single select (or deselect if already the only selection)
          if (next.size === 1 && next.has(key)) {
            next.clear()
          } else {
            next.clear()
            next.add(key)
          }
        }

        lastClickedKeyRef.current = key
        return next
      })
    },
    [allKeysInOrder],
  )

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set())
  }, [])

  // --- Chapter drag-and-drop ---

  const reorderChapters = useCallback(
    (activeChapterId: string, overChapterId: string) => {
      if (activeChapterId === overChapterId) {
        return
      }
      pushUndo()
      setDraft((prev) => {
        const oldIdx = prev.chapters.findIndex((ch) => ch.id === activeChapterId)
        const newIdx = prev.chapters.findIndex((ch) => ch.id === overChapterId)
        if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) {
          return prev
        }
        return { ...prev, chapters: arrayMove(prev.chapters, oldIdx, newIdx) }
      })
    },
    [pushUndo],
  )

  // --- Section drag-and-drop (multi-select aware) ---

  /** Find which container (chapter id or '__unattached__') a section key belongs to */
  const findContainer = useCallback(
    (sectionKey: string): string | null => {
      for (const ch of draft.chapters) {
        if (ch.chapterSections.includes(sectionKey)) {
          return ch.id
        }
      }
      if (draft.unattached.includes(sectionKey)) {
        return UNATTACHED_ID
      }
      return null
    },
    [draft],
  )

  const getSectionList = useCallback(
    (containerId: string): string[] => {
      if (containerId === UNATTACHED_ID) {
        return draft.unattached
      }
      return draft.chapters.find((ch) => ch.id === containerId)?.chapterSections ?? []
    },
    [draft],
  )

  /** Get the keys being dragged: if the active item is selected, all selected keys; otherwise just the active */
  const getDraggedKeys = useCallback(
    (activeId: string): string[] => {
      if (selectedKeys.has(activeId) && selectedKeys.size > 1) {
        // Return selected keys in their current order within their containers
        return allKeysInOrder.filter((k) => selectedKeys.has(k))
      }
      return [activeId]
    },
    [selectedKeys, allKeysInOrder],
  )

  const handleDragOver = useCallback(
    (activeId: string, overId: string, overContainerId: string) => {
      const fromContainer = findContainer(activeId)
      if (!fromContainer || fromContainer === overContainerId) {
        return
      }

      const draggedKeys = getDraggedKeys(activeId)

      pushUndo()
      setDraft((prev) => {
        const next = deepClone(prev)
        const draggedSet = new Set(draggedKeys)

        // Remove dragged keys from all containers
        for (const ch of next.chapters) {
          ch.chapterSections = ch.chapterSections.filter((k) => !draggedSet.has(k))
        }
        next.unattached = next.unattached.filter((k) => !draggedSet.has(k))

        // Insert into target container
        const toList =
          overContainerId === UNATTACHED_ID
            ? next.unattached
            : next.chapters.find((ch) => ch.id === overContainerId)!.chapterSections

        const toIdx = toList.indexOf(overId)
        if (toIdx === -1) {
          toList.push(...draggedKeys)
        } else {
          toList.splice(toIdx, 0, ...draggedKeys)
        }

        return next
      })
    },
    [findContainer, getDraggedKeys, pushUndo],
  )

  const handleDragEnd = useCallback(
    (activeId: string, overId: string | null) => {
      if (!overId) {
        return
      }
      const containerId = findContainer(activeId)
      if (!containerId) {
        return
      }

      const list = getSectionList(containerId)
      const oldIdx = list.indexOf(activeId)
      const newIdx = list.indexOf(overId)

      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) {
        return
      }

      const draggedKeys = getDraggedKeys(activeId)

      pushUndo()
      setDraft((prev) => {
        const next = deepClone(prev)
        const targetList =
          containerId === UNATTACHED_ID
            ? next.unattached
            : next.chapters.find((ch) => ch.id === containerId)!.chapterSections

        const draggedSet = new Set(draggedKeys)
        // Remove all dragged keys
        const filtered = targetList.filter((k) => !draggedSet.has(k))
        // Find where overId ended up in the filtered list
        const overIdxInFiltered = filtered.indexOf(overId)
        if (overIdxInFiltered === -1) {
          filtered.push(...draggedKeys)
        } else {
          // When moving down (active was before over), insert AFTER overId so
          // the dragged item can reach the last slot. Moving up inserts BEFORE.
          const insertIdx = oldIdx < newIdx ? overIdxInFiltered + 1 : overIdxInFiltered
          filtered.splice(insertIdx, 0, ...draggedKeys)
        }

        if (containerId === UNATTACHED_ID) {
          next.unattached = filtered
        } else {
          next.chapters.find((ch) => ch.id === containerId)!.chapterSections = filtered
        }

        return next
      })
    },
    [findContainer, getSectionList, getDraggedKeys, pushUndo],
  )

  const undo = useCallback(() => {
    const prev = undoStackRef.current.pop()
    if (prev) {
      setDraft(prev)
    }
  }, [])

  const canUndo = undoStackRef.current.length > 0

  return {
    draftChapters: draft.chapters,
    draftUnattached: draft.unattached,
    selectedKeys,
    dirty,
    canUndo,
    reorderChapters,
    toggleSelection,
    clearSelection,
    findContainer,
    getDraggedKeys,
    handleDragOver,
    handleDragEnd,
    undo,
  }
}

function deepClone<T>(val: T): T {
  return JSON.parse(JSON.stringify(val))
}
